/**
 * fighterDroneFire.ts
 * Pool + spawn/update helpers for `FighterDroneLaser` bolts.
 * Sibling of `bossTurret.ts` — same shape (module-singleton pool,
 * acquire-on-fire, per-tick update + cull, bulk-release on scene
 * teardown), different source entity.
 *
 * Owns:
 *  - The `FighterDroneLaser` pool (capped at
 *    `FIGHTER_DRONE_LASER_POOL_CAP`, pre-warmed at module load),
 *  - `fireFighterDroneLaser(x, y)` — single-shot acquire/spawn,
 *  - `updateFighterDroneLasers(dt)` — per-tick integrate + cull,
 *  - `releaseAllFighterDroneLasers()` — bulk release on scene teardown.
 *
 * Kept separate from `spaceEnemySystem` so `FighterDrone.update()` can
 * import the fire helper without dragging the enemy spawner / cull
 * pipeline into the entity layer.
 *
 * NO React imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module fighterDroneFire
 */

import { spaceState } from './spaceState';
import type { LevelRuntime } from './gameState';
import { FighterDroneLaser } from '../entities/FighterDroneLaser';
import { ObjectPool } from '../utils/objectPool';
import { FIGHTER_DRONE_LASER_POOL_CAP } from '../../constants/spaceEnemyConstants';

/** Reusable empty level for the laser update — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/**
 * Module-level fighter-laser pool. Pre-warmed at module load so the
 * first volley doesn't pay an allocation cost. Sized to
 * `FIGHTER_DRONE_LASER_POOL_CAP` — a finite ceiling above peak demand,
 * so `acquire()` returning `null` is a sign the cadence was tuned too
 * aggressively rather than a normal occurrence.
 */
const fighterLaserPool = new ObjectPool<FighterDroneLaser>(
  () => new FighterDroneLaser(),
  FIGHTER_DRONE_LASER_POOL_CAP,
);
fighterLaserPool.prewarm(FIGHTER_DRONE_LASER_POOL_CAP);

/**
 * Test-only helper: number of fighter-laser instances currently parked
 * in the pool (i.e. NOT live in `spaceState.fighterLasers`).
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _fighterLaserPoolAvailable(): number {
  return fighterLaserPool.available;
}

/**
 * Spawn a single fighter-drone laser bolt at the given world-space
 * coordinate. Returns `true` when a bolt was spawned, `false` when
 * the pool was exhausted (very unlikely at greybox cadence — see the
 * sizing rationale on {@link FIGHTER_DRONE_LASER_POOL_CAP}). Callers
 * use the return value so a pool miss doesn't burn the firing drone's
 * cadence reset.
 *
 * @param x - World-space X of the bolt's spawn (top-left of hitbox).
 * @param y - World-space Y of the bolt's spawn (top-left of hitbox).
 * @returns `true` when a bolt was spawned, `false` on a pool miss.
 */
export function fireFighterDroneLaser(x: number, y: number): boolean {
  const bolt = fighterLaserPool.acquire();
  if (bolt === null) return false;
  bolt.spawn(x, y);
  spaceState.fighterLasers.push(bolt);
  return true;
}

/**
 * Update every active fighter-drone laser and release dead ones back
 * to the pool. Mirrors `updateBossProjectiles` — separate from
 * `updateSpaceEnemies` so the laser stream keeps culling even after
 * the firing drone is destroyed (a kill shouldn't visually "freeze"
 * the in-flight bolts).
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateFighterDroneLasers(dt: number): void {
  const arr = spaceState.fighterLasers;
  for (let i = arr.length - 1; i >= 0; i--) {
    const bolt = arr[i];
    bolt.update(dt, EMPTY_SPACE_LEVEL);
    if (bolt.dead) {
      fighterLaserPool.release(bolt);
      arr.splice(i, 1);
    }
  }
}

/**
 * Release every live fighter-drone laser back to the pool and clear
 * the scene's bookkeeping list. Called from `SpaceScene.init()` and
 * `SpaceScene.destroy()` so a re-init of the space level starts from
 * a clean slate.
 */
export function releaseAllFighterDroneLasers(): void {
  for (let i = 0; i < spaceState.fighterLasers.length; i++) {
    fighterLaserPool.release(spaceState.fighterLasers[i]);
  }
  spaceState.fighterLasers.length = 0;
}
